import clsx from "clsx";
import AppLayout from "../layouts/AppLayout";
import StoreLogo from "../../components/StoreLogo";
import { Chart } from "../components/Chart";
import { Tabs } from "../components/Tabs";
import ImageGallery from "../components/ImageGallery";
import OfferItem from "../components/OfferItem";
import { Head, Link } from "@inertiajs/react";
import { useEffect, useMemo } from "react";
import { useAppState } from "../contexts/AppContext";
import Carousel from "../components/Carousel";
import JsonLdScript from "../components/JsonLdScript";
import DifferenceTag from "../components/DifferenceTag";
import PriceStats from "../components/PriceStats";

export default function Product({ product: { data: product }, temp, tempValues, lastViewedProducts, user, collections }) {
    const { setAppState } = useAppState()

    let less: number | null = null;
    let more: number | null = null;
    let diff: number | null = null;

    for (let i = 0; i < product.storeProducts.length; i++) {
        if (less === null && product.storeProducts[i].available) {
            less = product.storeProducts[i].price.sale_price;
        }

        const reverseIndex = product.storeProducts.length - 1 - i;

        if (more === null && product.storeProducts[reverseIndex].available) {
            more = product.storeProducts[reverseIndex].price.sale_price;
        }

        if (more !== null && less !== null) {
            break;
        }
    }

    if (less && product.averagePrice && product.averagePrice !== less) {
        diff = Math.round((product.averagePrice * 100 / less) - 100);
    }

    // useEffect(() => {
    //     setAppState({
    //         description: 
    //     })
    // }, [])

    let structuredData: any = {
        "@context": "https://schema.org/",
        "@type": "Product",
        "name": product.name,
        "description": product.shortDescription,
        "sku": product.sku,
        "mpn": product.sku,
    };

    if (product.primaryImage) {
        structuredData.image = product.primaryImage.url
    }

    if (product.brand) {
        structuredData.brand = {
            "@type": "Brand",
            "name": product.brand.name
        }
    }

    if (product.storeProducts) {
        structuredData.offers = product.storeProducts.map((offer) => {
            return {
                "@type": "Offer",
                "priceCurrency": "CAD",
                "price": offer.price.sale_price / 100,
                "availability": offer.available ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
                "url": `https://pricehunt.ca/link/${offer.id}`,
                "seller": {
                    "@type": "Organization",
                    "name": offer.store.name
                }
            }
        })

    }

    const productSeries = useMemo(() => {
        let temp = product.series.length > 0 && product.series[0].variants == true
            ? product.series[0].product_series
            : [];

        if (!Array.isArray(temp)) {
            temp = Object.values(temp);
        }

        temp.sort((a, b) => {
            if (a.product.attributeValue && b.product.attributeValue) {
                return a.product.attributeValue.localeCompare(b.product.attributeValue);
            }
            return 0;
        })

        return temp
    }, [product.series])

    return (
        <AppLayout user={user}>
            <JsonLdScript data={structuredData} />
            <Head>
                <title>{`${product.name} - Compare Prices in Canada | PriceHunt.ca`}</title>
                <meta head-key="description" name="description" content={`Compare prices for ${product.name} across Canadian stores. Updated daily. Find the best deal on PriceHunt.ca.`} />
                <link head-key="canonical" rel="canonical" href={`https://pricehunt.ca/products/${product.alias}`} />
            </Head>
            <div className="w-full">
                <div className="container max-w-dasha mx-auto px-4 sm:px-6 lg:px-8 sm:py-5 md:py-10 lg:py-20 lg:pt-0 flex flex-col gap-y-7">
                    <article className="flex flex-col gap-y-5">
                        <div>
                            <ul className="flex space-x-2">
                                <li>
                                    <Link href="/">Home</Link>
                                </li>
                                <li>/</li>
                                <li>
                                    <Link href={`/categories/${product.categories[0].alias}`}>{product.categories[0].name}</Link>
                                </li>
                            </ul>
                        </div>
                        {collections ? (
                            <div className="flex flex-wrap gap-4">
                                {collections.map((collection) => {
                                    return <Link className={`px-3 py-1 border rounded-md text-sm bg-white text-gray-800`} href={`/collections/${collection.alias}`}>
                                        <div className="flex flex-col items-center">
                                            {collection.name}
                                        </div>
                                    </Link>
                                })}
                            </div>
                        ) : null}

                        <div className="grid grid-cols-1 gap-y-10 gap-x-10 md:space-y-0 md:grid-cols-2">
                            <div className="md:hidden">
                                <div className="flex justify-between items-start ">
                                    <h1 className="text-xl font-medium">{product.name}</h1>
                                    {user ? <a className="px-6 py-1 bg-blue-500 text-white" href={`https://admin.pricehunt.ca/products/${product.id}`}>Edit</a> : null}
                                </div>
                                <div className="text-sm flex flex-wrap gap-2">
                                    <div>
                                        <span>SKU:</span> <span className="text-gray-600">{product.sku}</span>
                                    </div>
                                    {product.categories.length > 0 ? (
                                        <div>
                                            <span>Category: </span>
                                            <Link className="text-blue-500" href={`/categories/${product.categories[0].alias}`}>{product.categories[0].name}</Link>
                                        </div>
                                    ) : null}
                                    {product.brand_id !== null ? (
                                        <div>
                                            <span>Brand: </span>
                                            <Link className="text-blue-500" href={`/categories/${product.categories[0].alias}?brands=${product.brand_id}`}>{product.brand?.name}</Link>
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                            <div className="">
                                <ImageGallery options images={product.media.map((image) => (
                                    image.url
                                ))} />
                            </div>
                            <div>
                                <div className="md:flex justify-between items-start hidden">
                                    <h1 className="text-3xl font-medium">{product.name}</h1>
                                    {user ? <a className="px-6 py-1 bg-blue-500 text-white" href={`https://admin.pricehunt.ca/products/${product.id}`}>Edit</a> : null}
                                </div>
                                <div className="text-sm md:flex hidden flex-wrap gap-2">
                                    <div>
                                        <span>SKU:</span> <span className="text-gray-600">{product.sku}</span>
                                    </div>
                                    {product.categories.length > 0 ? (
                                        <div>
                                            <span>Category: </span>
                                            <Link className="text-blue-500" href={`/categories/${product.categories[0].alias}`}>{product.categories[0].name}</Link>
                                        </div>
                                    ) : null}
                                    {product.brand_id !== null ? (
                                        <div>
                                            <span>Brand: </span>
                                            <Link className="text-blue-500" href={`/categories/${product.categories[0].alias}?brands=${product.brand_id}`}>{product.brand?.name}</Link>
                                        </div>
                                    ) : null}

                                </div>

                                {product.series.length > 0 && product.series[0].variants == true ? (
                                    <div className="mt-5">
                                        <h2 className="text-lg mb-2">{product.series[0].variant_type}</h2>
                                        <div className="flex flex-wrap gap-4">
                                            {productSeries.map((productSeria) => {
                                                if (productSeria.product.attributeValue) {
                                                    return <Link replace className={`px-3 py-1 border rounded-md text-sm ${productSeria.product.id === product.id
                                                        ? 'bg-blue-500 text-white'
                                                        : 'bg-white text-gray-800'}`} href={`/products/${productSeria.product.alias}`}>
                                                        <div className="flex flex-col items-center">
                                                            <div>{productSeria.product.attributeValue}</div>
                                                            {productSeria.product.price ? (
                                                                <div>${Number(productSeria.product.price / 100).toFixed(2)}</div>
                                                            ) : (<div>Out of stock</div>)}

                                                        </div>
                                                    </Link>
                                                } else if (user) {
                                                    return <Link replace className={`px-3 py-1 border rounded-md text-sm ${productSeria.product.id === product.id
                                                        ? 'bg-blue-500 text-white'
                                                        : 'bg-white text-gray-800'}`} href={`/products/${productSeria.product.alias}`}>Only admin see (no attribute values)</Link>

                                                } else {
                                                    return null
                                                }

                                            })}
                                        </div>
                                    </div>
                                ) : null}
                                {/* {product.amazon === false ? (
                                <div>
                                    <a href={`https://admin.pricehunt.titarlab.com/products/${product.id}/amazon`}>Add amazon link</a>
                                </div>
                            ) : null} */}

                                {less && more ? (
                                    <div className="mt-5">
                                        {product.availableOptions > 1 ? (
                                            <>
                                                <div className="">
                                                    <span className="font-semibold">Options</span>: {product.availableOptions}
                                                </div>
                                                <div className="text-2xl font-semibold">
                                                    {less !== more ? (
                                                        <>
                                                            <span className="">${Number(less / 100).toFixed(2)}</span> - <span className="">{Number(more / 100).toFixed(2)}</span>
                                                            <DifferenceTag value={diff} />
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span className="">${Number(less / 100).toFixed(2)}</span>
                                                            <DifferenceTag value={diff} />
                                                        </>
                                                    )}

                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <span className="text-2xl font-semibold">${Number(less / 100).toFixed(2)}</span>
                                                <DifferenceTag value={diff} />
                                            </>
                                        )}
                                    </div>
                                ) : null}

                                {/* <PriceStats storeProducts={product.storeProducts} /> */}

                                <div className="mt-10">
                                    {product.storeProducts.length > 0 ? (
                                        <>
                                            <h2 className="text-lg mb-2">Where to buy</h2>
                                            <div className="space-y-4 ">



                                                {product.storeProducts ? product.storeProducts.map((storeProduct) => {
                                                    const avaiable = storeProduct.price.sale_price > 0 && storeProduct.available;

                                                    return (
                                                        <OfferItem
                                                            storeName={storeProduct.store.name}
                                                            marketplace={storeProduct.marketplace}
                                                            storeProductId={storeProduct.id}
                                                            salePrice={storeProduct.price.sale_price}
                                                            available={avaiable}
                                                            user={user}
                                                            originalLink={storeProduct.url}
                                                            updatedAt={storeProduct.price.updated_at}
                                                            isBest={storeProduct.price.sale_price === less}
                                                            isAffiliate={storeProduct.store.id === 2}
                                                        />
                                                    )
                                                }) : null}
                                            </div>
                                        </>
                                    ) : (
                                        <p className="text-gray-600 mt-4">
                                            This product is currently unavailable in all listed stores.
                                        </p>
                                    )}
                                </div>

                                {/* {product.shortDescription ? (
                                <div className="mt-5">
                                    <h2 className="text-lg">Description</h2>
                                    {product.shortDescription}
                                </div>
                            ) : null} */}

                                {/* <h3 className="text-2xl font-semibold mb-4">Price history</h3>
                            <Chart values={tempValues} labels={temp} /> */}
                            </div>
                        </div>

                        <Tabs
                            description={product.shortDescription}
                            attributes={product.categories[0] && product.categories[0].attributes}
                            values={product.attributes}
                            historyValues={tempValues}
                            historyLabels={temp}
                        />

                    </article>

                    {lastViewedProducts?.length > 0 ? (
                        <div className="mt-5 mb-5">
                            <h2 className="text-lg mb-2">Last viewed products</h2>
                            <Carousel options={{ dragFree: true }} slides={lastViewedProducts} />
                        </div>
                    ) : null}

                    <section className="">
                        <h2 className="text-lg mb-2">Track Prices and Compare Deals in Canada</h2>
                        <p>Looking to get the best price for the <strong>{product.name}</strong>?<br />PriceHunt.ca helps you compare prices across Canada’s top electronics retailers in real-time. We monitor price drops, special offers, and exclusive deals, so you never overpay for your tech.</p>
                        {/* <p>Enable price alerts to receive notifications when the price of this product changes. Whether you're shopping at BestBuy, Amazon, or other Canadian stores, our price tracker ensures you get the best deal available.</p> */}
                        <p>PriceHunt.ca is your trusted platform for finding the lowest prices on TVs, laptops, CPUs, and more — all in one place. Compare features, read product specifications, and make informed decisions before you buy.</p>
                    </section>
                </div>



            </div >
        </AppLayout >
    )
}